import { Collection } from "../types";
import { useI18n } from "../i18n";
import { SettingsMenu } from "./SettingsMenu";
import coinImg from "../assets/reward coin.png";
import "./CollectionsScreen.css";

function getPreviewImageUrls(baseUrl: string): { webp: string; jpeg: string } {
  if (baseUrl.startsWith("/images/pexels-") && !baseUrl.includes(".")) {
    // For previews, use smallest size
    return {
      webp: `${baseUrl}-480.webp`,
      jpeg: `${baseUrl}-480.jpeg`,
    };
  }
  return { webp: baseUrl, jpeg: baseUrl };
}

interface CollectionProgress {
  completed: number;
  total: number;
}

interface CollectionsScreenProps {
  collections: Collection[];
  currentCoins: number;
  currentCollectionId: string;
  completionData: { [id: string]: CollectionProgress };
  onSelectCollection: (id: string) => void;
  onPurchaseCollection: (id: string) => void;
  onReplayCollection: (id: string) => void;
}

export function CollectionsScreen({
  collections,
  currentCoins,
  currentCollectionId,
  completionData,
  onSelectCollection,
  onPurchaseCollection,
  onReplayCollection,
}: CollectionsScreenProps) {
  const { t, getCollectionName } = useI18n();

  return (
    <div className="collections-screen">
      <div className="collections-header-wrapper">
        <div className="collections-header">
          <div className="coins-display">
            <img src={coinImg} alt="Coins" />
            <span>{currentCoins}</span>
          </div>
          <SettingsMenu inline />
        </div>
      </div>

      <div className="collections-grid">
        {[...collections]
          .sort((a, b) => {
            const progressA = completionData[a.id] || {
              completed: 0,
              total: a.levels.length,
            };
            const progressB = completionData[b.id] || {
              completed: 0,
              total: b.levels.length,
            };
            const isCompleteA = progressA.completed >= progressA.total;
            const isCompleteB = progressB.completed >= progressB.total;

            // Priority: 1) Playable (unlocked, not complete), 2) Buyable (locked, can afford), 3) Locked (can't afford), 4) Complete
            const canAffordA = currentCoins >= (a.price || 0);
            const canAffordB = currentCoins >= (b.price || 0);

            const getScore = (
              c: Collection,
              isComplete: boolean,
              canAfford: boolean,
            ) => {
              if (c.unlocked && !isComplete) return 0; // Playable - highest priority
              if (!c.unlocked && canAfford) return 1; // Buyable
              if (!c.unlocked && !canAfford) return 2; // Locked, can't afford
              return 3; // Complete - lowest priority
            };

            const scoreA = getScore(a, isCompleteA, canAffordA);
            const scoreB = getScore(b, isCompleteB, canAffordB);

            return scoreA - scoreB;
          })
          .map((collection) => {
            const progress = completionData[collection.id] || {
              completed: 0,
              total: collection.levels.length,
            };
            const isComplete = progress.completed >= progress.total;
            const isCurrent = collection.id === currentCollectionId;
            const canAfford = currentCoins >= (collection.price || 0);

            return (
              <div
                key={collection.id}
                className={`collection-card ${isCurrent ? "current" : ""} ${!collection.unlocked ? "locked" : ""} ${isComplete ? "complete" : ""}`}
              >
                <div className="card-preview">
                  {collection.levels[0] &&
                    (() => {
                      const urls = getPreviewImageUrls(
                        collection.levels[0].imageUrl,
                      );
                      return (
                        <picture>
                          <source srcSet={urls.webp} type="image/webp" />
                          <img
                            src={urls.jpeg}
                            alt={collection.name}
                            className="preview-image"
                          />
                        </picture>
                      );
                    })()}
                </div>

                <div className="card-content">
                  <h3>{getCollectionName(collection.id) || collection.name}</h3>

                  {collection.unlocked ? (
                    <>
                      <div className="progress-bar">
                        <div
                          className={`progress-fill ${isComplete ? "complete" : ""}`}
                          style={{
                            width: `${(progress.completed / progress.total) * 100}%`,
                          }}
                        />
                      </div>
                      <div className="progress-text">
                        {isComplete
                          ? `✓ ${t.complete}`
                          : `${progress.completed}/${progress.total}`}
                      </div>
                    </>
                  ) : (
                    <div className="locked-info">
                      <span className="lock-icon">🔒</span>
                      <span className="price">
                        <img src={coinImg} alt="" />
                        {collection.price}
                      </span>
                    </div>
                  )}

                  <div className="card-action">
                    {collection.unlocked ? (
                      isComplete ? (
                        <button
                          className="replay-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            onReplayCollection(collection.id);
                          }}
                        >
                          🔄 {t.replay}
                        </button>
                      ) : (
                        <button
                          className={`play-btn ${isCurrent ? "playing" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isCurrent) onSelectCollection(collection.id);
                          }}
                        >
                          {isCurrent ? t.playing : t.play}
                        </button>
                      )
                    ) : (
                      <button
                        className={`buy-btn ${!canAfford ? "disabled" : ""}`}
                        disabled={!canAfford}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (canAfford) onPurchaseCollection(collection.id);
                        }}
                      >
                        {canAfford ? t.buy : t.needMore}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}
